import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import api, { apiErr, fmtMoney, fileUrl } from "../lib/api";
import { getCache, setCache, hasCache } from "../lib/swr";
import { useAuth } from "../context/AuthContext";
import { Loader2, MapPin, Home as HomeIcon, ArrowRight, ArrowUpRight, HandHeart, HandCoins, Receipt, Heart, Users, MessageSquare, ChevronRight, ExternalLink, TrendingUp, TrendingDown, AlertTriangle, Check, X } from "lucide-react";
import TripGlobe from "../components/TripGlobe";
import TripCountdown from "../components/TripCountdown";
import ShareJourney from "../components/ShareJourney";
import ShareInvite from "../components/ShareInvite";
import DailyMission from "../components/DailyMission";
import Announcements from "../components/Announcements";
import GettingStarted from "../components/GettingStarted";
import TripWrapUp from "../components/TripWrapUp";
import { getTripStatus } from "../lib/tripStatus";
import { flagEmoji } from "../lib/flags";
import PartnerHome from "../components/PartnerHome";
import GiveDialog from "../components/GiveDialog";
import { openGive } from "../lib/give";

export default function Home() {
  const { user, org, config, can, canViewFinancials } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(() => getCache("home:data") || null);
  const [loading, setLoading] = useState(() => !hasCache("home:data"));
  const [giveOpen, setGiveOpen] = useState(false);

  const isPartner = user?.role === "partner";
  const giveUrl = config?.donate_url || (org?.slug === "reset" ? "https://akop.live/give" : "");

  useEffect(() => {
    if (isPartner) { setLoading(false); return; }
    (async () => {
      try {
        const isAdmin = user?.role === "admin";
        const calls = [api.get("/budget/summary"), api.get("/dispatches"), api.get("/prayers"), api.get("/itinerary/countries"), api.get("/itinerary/entries")];
        calls.push(isAdmin ? api.get("/followers/count") : api.get("/impact"));
        if (isAdmin) { calls.push(api.get("/gifts")); calls.push(api.get("/expenses")); calls.push(api.get("/budget/items")); }
        const res = await Promise.all(calls);
        const payload = {
          budget: res[0].data, dispatches: res[1].data, prayers: res[2].data, countries: res[3].data, entries: res[4].data,
          impact: isAdmin ? undefined : res[5]?.data,
          partnerCount: isAdmin ? (res[5]?.data?.count ?? 0) : undefined,
          gifts: isAdmin ? (res[6]?.data ?? []) : undefined,
          expenses: isAdmin ? (res[7]?.data ?? []) : undefined,
          budgetItems: isAdmin ? (res[8]?.data ?? []) : undefined,
        };
        setData(payload);
        setCache("home:data", payload);
      } catch (e) { toast(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [user, isPartner]);

  if (isPartner) return <PartnerHome />;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  const cur = data?.budget?.currency || "USD";
  const goal = data?.budget?.goal_cents || 0;
  const raised = data?.budget?.raised_cents || 0;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const latest = data?.dispatches?.[0];

  // trip status (auto: manual "Currently here" > calendar dates > home)
  const cleanName = (n) => (n || "").replace(/[^\p{L}\s,]/gu, "").split(",").pop().trim();
  const origin = config?.origin_lat != null && config?.origin_lng != null
    ? { lat: config.origin_lat, lng: config.origin_lng, country: cleanName(config.origin_country), city: config.origin_city }
    : null;
  const tripStatus = getTripStatus(config, data?.countries || [], origin);

  const today = new Date();
  const start = config?.start_date ? new Date(config.start_date + "T00:00:00") : null;
  const end = config?.end_date ? new Date(config.end_date + "T23:59:59") : null;

  // Countdown target: the scheduled departure from home base (earliest flight), else the day of departure.
  const departEntry = (data?.entries || [])
    .filter((e) => e.entry_type === "flight" && e.date)
    .sort((a, b) => `${a.date}T${a.time || "00:00"}`.localeCompare(`${b.date}T${b.time || "00:00"}`))[0];
  const departTarget = departEntry
    ? `${departEntry.date}T${departEntry.time ? (departEntry.time.length === 5 ? departEntry.time + ":00" : departEntry.time) : "00:00:00"}`
    : (config?.start_date ? `${config.start_date}T00:00:00` : null);
  let dayLabel = "";
  if (start) {
    const nowMs = today.getTime();
    if (nowMs < start.getTime()) {
      const dd = Math.floor((start.getTime() - nowMs) / 86400000);
      dayLabel = `${dd} ${dd === 1 ? "day" : "days"} to go`;
    } else if (!end || nowMs <= end.getTime()) {
      dayLabel = `Day ${Math.floor((nowMs - start.getTime()) / 86400000) + 1}`;
    }
  }

  const firstName = (user?.name || "").split(" ")[0];
  const weekAgo = Date.now() - 7 * 86400000;
  const raisedThisWeek = (data?.gifts || []).reduce((s, g) => {
    const t = new Date(g.date || g.created_at || 0).getTime();
    return t >= weekAgo ? s + (g.amount_cents || 0) : s;
  }, 0);
  const spentThisWeek = (data?.expenses || []).reduce((s, e) => {
    const t = new Date(e.date || e.created_at || 0).getTime();
    return t >= weekAgo ? s + (e.amount_cents || 0) : s;
  }, 0);
  const prevWeekStart = weekAgo - 7 * 86400000;
  const raisedPrevWeek = (data?.gifts || []).reduce((s, g) => {
    const t = new Date(g.date || g.created_at || 0).getTime();
    return (t >= prevWeekStart && t < weekAgo) ? s + (g.amount_cents || 0) : s;
  }, 0);
  const raisedTrend = raisedPrevWeek > 0
    ? { dir: raisedThisWeek >= raisedPrevWeek ? "up" : "down", pct: Math.abs(Math.round(((raisedThisWeek - raisedPrevWeek) / raisedPrevWeek) * 100)) }
    : (raisedThisWeek > 0 ? { dir: "up", pct: null } : null);
  const spentPrevWeek = (data?.expenses || []).reduce((s, e) => {
    const t = new Date(e.date || e.created_at || 0).getTime();
    return (t >= prevWeekStart && t < weekAgo) ? s + (e.amount_cents || 0) : s;
  }, 0);
  const spentTrend = spentPrevWeek > 0
    ? { dir: spentThisWeek >= spentPrevWeek ? "up" : "down", pct: Math.abs(Math.round(((spentThisWeek - spentPrevWeek) / spentPrevWeek) * 100)) }
    : (spentThisWeek > 0 ? { dir: "up", pct: null } : null);

  // Fundraising pace (admin only): compares raised vs expected over a ~90-day campaign ending at departure.
  const departMs = departTarget ? new Date(departTarget).getTime() : null;
  let pace = null;
  if (user?.role === "admin" && goal > 0 && departMs && departMs > Date.now()) {
    const campaignStart = departMs - 90 * 86400000;
    const elapsed = Math.min(1, Math.max(0, (Date.now() - campaignStart) / (departMs - campaignStart)));
    const behindPoints = Math.round((elapsed - raised / goal) * 100);
    const daysLeft = Math.max(0, Math.ceil((departMs - Date.now()) / 86400000));
    pace = { active: true, behind: behindPoints >= 10, behindPoints, daysLeft };
  }
  const globeMarkers = (data?.countries || [])
    .filter((c) => typeof c.lat === "number" && typeof c.lng === "number")
    .map((c) => ({ lat: c.lat, lng: c.lng, current: c.id === tripStatus.currentId, name: cleanName(c.name) }));
  const countryFlags = (data?.countries || [])
    .map((c) => ({ name: cleanName(c.name), flag: flagEmoji(c.name) }))
    .filter((f) => f.flag);

  const overBudget = (() => {
    if (user?.role !== "admin") return [];
    const spentByCat = {};
    (data?.expenses || []).forEach((e) => { spentByCat[e.category] = (spentByCat[e.category] || 0) + (e.amount_cents || 0); });
    return (data?.budgetItems || [])
      .map((i) => ({ category: i.category, planned: i.planned_cents || 0, spent: spentByCat[i.category] || 0 }))
      .filter((i) => i.planned > 0 && i.spent > i.planned)
      .map((i) => ({ ...i, over: i.spent - i.planned }))
      .sort((a, b) => b.over - a.over);
  })();
  const totalOver = overBudget.reduce((s, i) => s + i.over, 0);

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm text-muted-foreground">{user?.role === "admin" ? "Welcome back," : "Welcome,"}</div>
        <h1 className="font-heading font-extrabold text-3xl text-[#0d2340]">{firstName || "Friend"} 👋</h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="home-welcome-note">
          {user?.role === "admin"
            ? `Here's how ${config?.trip_name || "your mission trip"} is coming together today.`
            : `Thank you for walking this journey with us. You're not following from a distance — you're part of what God is doing.`}
        </p>
      </div>

      {user?.role !== "admin" && <WhyYoureHere orgName={org?.org_name} userId={user?.id} />}

      {user?.role === "admin" && overBudget.length > 0 && (
        <button data-testid="home-overbudget-alert" onClick={() => navigate("/app/budget?tab=budget")}
          className="w-full text-left rounded-2xl p-4 flex items-start gap-3 border border-red-200 bg-red-50 hover:bg-red-100/70 transition-colors">
          <span className="h-9 w-9 rounded-xl bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-red-600" /></span>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[#2C2C3E]">{overBudget.length} {overBudget.length === 1 ? "category is" : "categories are"} over budget</div>
            <div className="text-sm text-red-700/90 mt-0.5 truncate">
              {overBudget.slice(0, 3).map((i) => `${i.category} +${fmtMoney(i.over, cur)}`).join(" · ")}{overBudget.length > 3 ? " …" : ""}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{fmtMoney(totalOver, cur)} over planned in total · tap to review</div>
          </div>
          <ChevronRight className="h-4 w-4 text-red-400 shrink-0 mt-1" />
        </button>
      )}

      {user?.role === "admin" && (
        <TripWrapUp stats={{ dispatches: data?.dispatches?.length, prayers: data?.prayers?.length, partners: data?.partnerCount, raised }} />
      )}

      <DailyMission isAdmin={user?.role === "admin"} />

      {user?.role === "admin" && config?.status !== "completed" && (
        <GettingStarted
          orgId={org?.id}
          countryCount={data?.countries?.length || 0}
          goalCents={goal}
          dispatchCount={data?.dispatches?.length || 0}
          partnerCount={data?.partnerCount || 0}
          donateUrl={config?.donate_url}
        />
      )}

      {/* Countdown hero */}
      <div className="rounded-3xl px-6 py-4 text-white shadow-[0_14px_40px_rgba(13,35,64,0.28)] relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0d2340 0%,#14365f 55%,#1f5290 100%)" }} data-testid="countdown-tile">
        {departTarget ? (
          <>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden>
              <svg width="66" height="72" viewBox="0 0 66 72" fill="none">
                <path d="M6 66 C 34 62, 18 34, 48 28" stroke="#E0A73A" strokeWidth="2" strokeDasharray="3 5" strokeLinecap="round" fill="none" opacity="0.85" />
              </svg>
              <MapPin className="absolute -top-1 right-1 h-7 w-7" style={{ color: "#E0A73A" }} fill="#E0A73A" />
            </div>
            <TripCountdown startDate={departTarget} endDate={config?.end_date} variant="plain" label="Departing in" />
          </>
        ) : (
          <div className="text-center py-1" data-testid="home-location">
            <div className="font-heading font-extrabold text-3xl leading-tight">{tripStatus.label || config?.trip_name || org?.org_name}</div>
            {tripStatus.atHome && <div className="text-xs text-white/70 mt-1">Home base · trip hasn't started yet</div>}
          </div>
        )}
      </div>

      {user?.role !== "admin" && start && (
        <p className="text-center text-[15px] text-[var(--brand-navy)] font-heading font-semibold -mt-1" data-testid="home-we-line">
          {Date.now() < start.getTime()
            ? `In ${Math.max(1, Math.ceil((start.getTime() - Date.now()) / 86400000))} days, WE begin the journey.`
            : (!end || Date.now() <= end.getTime())
              ? `WE're on the journey — day ${Math.floor((Date.now() - start.getTime()) / 86400000) + 1}.`
              : `WE walked it together — thank you.`}
        </p>
      )}
      {(globeMarkers.length > 0 || origin) && (
        <div className="rounded-3xl px-5 pt-5 pb-5 shadow-[0_8px_30px_rgba(37,99,235,0.10)] relative overflow-hidden" style={{ background: "linear-gradient(180deg,#EEF4FF,#FFFFFF)" }} data-testid="home-globe-card">
          <div className="flex items-start justify-between">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#2563eb" }}>The journey</div>
            <div className="text-xs" style={{ color: "#8A97A6" }}>How the mission is unfolding.</div>
          </div>
          <div className="flex justify-center mt-1" style={{ maskImage: "linear-gradient(to bottom, #000 38%, transparent 70%)", WebkitMaskImage: "linear-gradient(to bottom, #000 38%, transparent 70%)" }}>
            <TripGlobe markers={globeMarkers} origin={origin} size={250} />
          </div>
          <div className="relative -mt-20 text-center">
            <div className="text-base font-semibold text-[#0d2340]">
              <span className="font-heading font-extrabold">{globeMarkers.length}</span> {globeMarkers.length === 1 ? "country" : "countries"} on the route
              {tripStatus.current ? <> · currently in <span className="accent-text font-semibold">{cleanName(tripStatus.current.name)}</span></>
                : origin ? <> · departing from <span className="accent-text font-semibold">{cleanName(origin.country)}</span></> : null}
            </div>
            {countryFlags.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-2 text-2xl" data-testid="route-flags">
                {countryFlags.map((f, i) => <span key={`${f.name}-${i}`} title={f.name} className="leading-none">{f.flag}</span>)}
              </div>
            )}
            <button data-testid="home-view-journey" onClick={() => navigate("/app/itinerary")}
              className="mt-4 mx-auto flex items-center justify-center gap-1.5 text-white rounded-full px-6 h-11 text-sm font-semibold transition-transform active:scale-[0.98] w-full max-w-[260px]" style={{ background: "linear-gradient(135deg,#2563eb,#0E7C86)" }}>
              View the journey <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <Announcements isAdmin={can("announcements")} />

      {/* Fundraising progress */}
      {canViewFinancials() && (
      <button data-testid="home-budget-card" onClick={() => navigate("/app/budget")} className="w-full text-left bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><HandCoins className="h-4 w-4 accent-text" /> {user?.role === "admin" ? "Raised toward goal" : "Together we've provided"}</div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex items-end justify-between mt-1">
          <div className="font-heading font-extrabold text-3xl text-[#0d2340]">{fmtMoney(raised, cur)}</div>
          <div className="text-sm text-muted-foreground">of {fmtMoney(goal, cur)}</div>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full transition-[width] duration-1000" style={{ width: `${pct}%`, background: pace?.behind ? "#E0913F" : "var(--tenant-accent)" }} />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-xs text-muted-foreground">{pct}% funded</div>
          {pace?.active && (
            pace.behind ? (
              <span data-testid="pace-pill" className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5"><AlertTriangle className="h-3 w-3" /> Behind pace</span>
            ) : (
              <span data-testid="pace-pill" className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5"><Check className="h-3 w-3" /> On pace</span>
            )
          )}
        </div>
        {pace?.behind && (
          <div data-testid="pace-note" className="text-[11px] text-amber-700 mt-2 leading-snug">
            About {pace.behindPoints}% behind pace — {pace.daysLeft} {pace.daysLeft === 1 ? "day" : "days"} to departure. Rally your partners.
          </div>
        )}
      </button>
      )}

      {/* Admin stats / Partner donate CTA */}
      {user?.role === "admin" ? (
        <div className="grid grid-cols-3 gap-2.5">
          <button data-testid="home-stat-partners" onClick={() => navigate("/app/partners")}
            className="text-left rounded-2xl p-3.5 text-white shadow-[0_6px_20px_rgba(13,35,64,0.18)] transition-transform hover:scale-[1.03]" style={{ background: "#0d2340" }}>
            <Users className="h-4 w-4" style={{ color: "#E0A73A" }} />
            <div className="font-heading font-bold text-lg mt-2 leading-none">{data?.partnerCount ?? 0}</div>
            <div className="text-[11px] opacity-80 mt-1 leading-tight">Partners following</div>
          </button>
          <button data-testid="home-stat-week" onClick={() => navigate("/app/gifts")}
            className="text-left rounded-2xl p-3.5 text-white shadow-[0_6px_20px_rgba(13,35,64,0.18)] transition-transform hover:scale-[1.03]" style={{ background: "#0d2340" }}>
            <div className="flex items-center justify-between">
              <HandCoins className="h-4 w-4" style={{ color: "#E0A73A" }} />
              {raisedThisWeek > 0 && raisedTrend && (
                <span data-testid="home-stat-week-trend" className="flex items-center gap-0.5 text-[10px] font-semibold bg-white/15 rounded-full px-1.5 py-0.5 leading-none">
                  {raisedTrend.dir === "up" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {raisedTrend.pct != null ? `${raisedTrend.pct}%` : "New"}
                </span>
              )}
            </div>
            <div className="font-heading font-bold text-lg mt-2 leading-none">{fmtMoney(raisedThisWeek, cur)}</div>
            <div className="text-[11px] opacity-80 mt-1 leading-tight">Raised this week</div>
          </button>
          <button data-testid="home-stat-spent" onClick={() => navigate("/app/budget")}
            className="text-left rounded-2xl p-3.5 text-white shadow-[0_6px_20px_rgba(13,35,64,0.18)] transition-transform hover:scale-[1.03]" style={{ background: "#0d2340" }}>
            <div className="flex items-center justify-between">
              <Receipt className="h-4 w-4" style={{ color: "#E0A73A" }} />
              {spentThisWeek > 0 && spentTrend && (
                <span data-testid="home-stat-spent-trend" className={`flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none ${spentTrend.dir === "up" && (spentTrend.pct == null || spentTrend.pct >= 25) ? "bg-amber-300 text-amber-950" : "bg-white/15 text-white"}`}>
                  {spentTrend.dir === "up" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {spentTrend.pct != null ? `${spentTrend.pct}%` : "New"}
                </span>
              )}
            </div>
            <div className="font-heading font-bold text-lg mt-2 leading-none">{fmtMoney(spentThisWeek, cur)}</div>
            <div className="text-[11px] opacity-80 mt-1 leading-tight">Spent this week</div>
          </button>
        </div>
      ) : giveUrl ? (
        <div
          data-testid="home-give-card"
          className="w-full rounded-3xl p-6 text-white shadow-[0_8px_32px_rgba(44,44,62,0.14)]"
          style={{ background: "var(--tenant-accent)" }}
        >
          <div className="flex items-center gap-2 text-sm opacity-90"><HandHeart className="h-4 w-4" /> Partner with this mission</div>
          <div className="font-heading font-extrabold text-2xl mt-1.5 leading-tight">{config?.donate_label || "Give toward the trip"}</div>
          <p className="text-sm opacity-90 mt-1">Your gift helps carry this journey forward — every bit makes a difference.</p>
          <button
            data-testid="home-give-btn"
            onClick={() => openGive(giveUrl)}
            className="mt-4 inline-flex items-center gap-2 bg-white text-[#2C2C3E] font-semibold rounded-full px-5 py-2.5 text-sm transition-transform hover:scale-[1.03]"
          >
            Give now <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      ) : null}
      <GiveDialog open={giveOpen} onOpenChange={setGiveOpen} donateUrl={config?.donate_url} donateLabel={config?.donate_label} orgSlug={org?.slug} />

      <ShareJourney org={org} config={config} />

      {user?.role === "admin" && <ShareInvite org={org} />}

      {/* Your impact (partner) — only once they've actually given */}
      {user?.role !== "admin" && data?.impact && data.impact.total_given_cents > 0 && (
        <button data-testid="home-impact-card" onClick={() => navigate("/app/impact")} className="w-full text-left rounded-2xl p-5 text-white shadow-[0_4px_24px_rgba(44,44,62,0.08)]" style={{ background: "var(--tenant-accent)" }}>
          <div className="flex items-center gap-2 text-sm opacity-90"><Heart className="h-4 w-4" fill="currentColor" /> Your impact</div>
          <div className="font-heading font-extrabold text-3xl mt-1">{fmtMoney(data.impact.total_given_cents, cur)}</div>
          <div className="text-sm opacity-90">across {data.impact.gift_count} {data.impact.gift_count === 1 ? "gift" : "gifts"} — thank you!</div>
        </button>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        <button data-testid="home-dispatches-stat" onClick={() => navigate("/app/feed")} className="bg-white rounded-2xl p-4 text-left shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
          <MessageSquare className="h-5 w-5 accent-text mb-2" />
          <div className="font-heading font-bold text-2xl text-[#2C2C3E]">{data?.dispatches?.length || 0}</div>
          <div className="text-xs text-muted-foreground">Dispatches</div>
        </button>
        <button data-testid="home-prayers-stat" onClick={() => navigate("/app/prayer")} className="bg-white rounded-2xl p-4 text-left shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
          <HandHeart className="h-5 w-5 accent-text mb-2" />
          <div className="font-heading font-bold text-2xl text-[#2C2C3E]">{data?.prayers?.length || 0}</div>
          <div className="text-xs text-muted-foreground">Prayer requests</div>
        </button>
      </div>

      {/* Because of people like you (partner) */}
      {user?.role !== "admin" && (
        <div className="rounded-2xl p-5 text-white shadow-[0_4px_24px_rgba(44,44,62,0.08)]" style={{ background: "var(--tenant-accent)" }} data-testid="home-because-card">
          <div className="text-sm font-semibold opacity-90">Because of people like you</div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div><div className="font-heading font-extrabold text-2xl leading-none">{data?.prayers?.length || 0}</div><div className="text-[11px] opacity-85 mt-1 leading-tight">Prayers shared</div></div>
            <div><div className="font-heading font-extrabold text-2xl leading-none">{data?.dispatches?.length || 0}</div><div className="text-[11px] opacity-85 mt-1 leading-tight">Dispatches</div></div>
            <div><div className="font-heading font-extrabold text-2xl leading-none">{data?.countries?.length || 0}</div><div className="text-[11px] opacity-85 mt-1 leading-tight">Countries ahead</div></div>
          </div>
        </div>
      )}

      {/* Latest dispatch */}
      {latest && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Latest dispatch</h2>
            <button data-testid="home-see-all" onClick={() => navigate("/app/feed")} className="text-xs font-medium accent-text">See all</button>
          </div>
          <button data-testid="home-latest-dispatch" onClick={() => navigate("/app/feed")} className="w-full text-left bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
            {latest.media?.length > 0 && <img src={fileUrl(latest.media[0], 800)} alt="" className="w-full h-40 object-cover" />}
            <div className="p-4">
              <h3 className="font-heading font-bold text-lg text-[#2C2C3E] leading-snug">{latest.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{latest.body}</p>
              <div className="text-xs text-muted-foreground mt-2">{latest.author_name} · {latest.created_at ? formatDistanceToNow(new Date(latest.created_at), { addSuffix: true }) : ""}</div>
            </div>
          </button>
        </div>
      )}

      {user?.role !== "admin" && (
        <p className="text-center text-sm text-muted-foreground italic pt-2 pb-1" data-testid="home-tagline">
          Some are called to go. Others are called to send. Together we fulfill the mission.
        </p>
      )}
    </div>
  );
}

function WhyYoureHere({ orgName, userId }) {
  const storageKey = `mc_why_${userId || "anon"}`;
  const [show, setShow] = useState(() => {
    try { return localStorage.getItem(storageKey) !== "1"; } catch { return true; }
  });
  if (!show) return null;
  const dismiss = () => { try { localStorage.setItem(storageKey, "1"); } catch {} setShow(false); };
  const items = ["Receive updates from the field", "Pray in real time", "Encourage the team", "Celebrate what God is doing", "See the journey unfold"];
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] border-l-4" style={{ borderColor: "var(--tenant-accent)" }} data-testid="why-here-card">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading font-bold text-lg text-[#0d2340]">Why you're here</h3>
        <button data-testid="why-here-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-[#2C2C3E]"><X className="h-4 w-4" /></button>
      </div>
      <p className="text-sm text-muted-foreground mt-1">You're walking alongside {orgName || "this mission"}. Through this app you'll:</p>
      <ul className="mt-3 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-center gap-2 text-sm text-[#2C2C3E]"><Check className="h-4 w-4 shrink-0" style={{ color: "var(--tenant-accent)" }} /> {it}</li>
        ))}
      </ul>
      <button data-testid="why-here-got-it" onClick={dismiss} className="mt-4 text-sm font-semibold accent-text hover:underline">Got it →</button>
    </div>
  );
}

function toast(msg) { import("sonner").then(({ toast }) => toast.error(msg)); }
